// Слой доступа к данным.
//
// ОДНО ПРАВИЛО, ИЗ КОТОРОГО СЛЕДУЕТ ВСЁ ОСТАЛЬНОЕ: приложение обязано
// открываться и показывать настоящие числа без сервера. Поэтому каждый
// вызов сначала стучится в API на 127.0.0.1:5177, а при любой неудаче —
// сервер не поднят, вернул 5xx, отдал не то — уходит на запечённый
// образец в public/offline/ и ЧЕСТНО помечает источник. Молчаливой
// подмены нет: у каждого куска данных есть поле origin, и интерфейс его
// показывает.
//
// Ошибку сервера показываем дословно (раздел 4 контракта), а не
// переписываем своими словами: переписанная ошибка теряет ровно ту
// подробность, ради которой её читают.

import type {
  BalanceConfig,
  Catalog,
  DatasetInfo,
  DetailRow,
  Preset,
  RoomSweepReport,
  RunRow,
  TelemetryReport,
} from '../lib/types';
import { parseTelemetry } from '../lib/telemetry';
import {
  normalizeBudgets,
  normalizeSchema,
  normalizeTuningResult,
  type BudgetEntry,
  type Overrides,
  type TuningResult,
  type TuningSchema,
} from '../lib/tuning';
import type { SpawnStatistics } from '../lib/spawns';
import type { ItemsPayload } from '../lib/items';
import { computeSpawns } from '../lib/spawns';
import {
  normalizeCatalog,
  normalizeComparison,
  normalizeDatasets,
  normalizeFindings,
  normalizeSimulate,
  normalizeSpawns,
  telemetryRawLines,
  type ComparisonExtras,
  type ServerFindings,
  type SimulateSummary,
} from './normalize';

export const API_BASE = '/api';
export const API_ORIGIN = 'http://127.0.0.1:5177/api';
// От корня приложения, а не относительно текущей страницы: с маршрутами-путями
// относительный 'offline' на любом вложенном пути запросил бы /<путь>/offline/….
const OFFLINE_BASE = `${import.meta.env.BASE_URL}offline`;

const PROBE_TIMEOUT_MS = 1200;
const READ_TIMEOUT_MS = 15_000;
const SIMULATE_TIMEOUT_MS = 60_000;
/** Потолок limit у сервера — 5000 строк на страницу. */
const RUNS_PAGE = 5000;

/** Ошибка, чей текст пришёл от сервера и показывается пользователю дословно. */
export class ApiError extends Error {
  /** Адрес поля из ошибки данных CLI (`400 {error, field}`): какой ползунок подсветить. */
  readonly field: string | null;

  constructor(message: string, readonly status: number, field: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.field = field;
  }
}

function fieldOf(payload: unknown): string | null {
  return payload !== null && typeof payload === 'object' && typeof (payload as { field?: unknown }).field === 'string'
    ? (payload as { field: string }).field
    : null;
}

async function withTimeout<T>(ms: number, run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await run(controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

/** Сырой вызов API. Бросает ApiError с текстом сервера или сетевую ошибку. */
async function api(path: string, init?: RequestInit, timeout = READ_TIMEOUT_MS): Promise<unknown> {
  const response = await withTimeout(timeout, (signal) =>
    fetch(API_BASE + path, { ...init, signal, headers: { Accept: 'application/json', ...(init?.headers ?? {}) } }),
  );
  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text.length > 0 ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }
  if (!response.ok) {
    const message =
      payload !== null && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : typeof payload === 'string' && payload.trim().length > 0
          ? payload.trim()
          : `сервер ответил ${response.status}`;
    throw new ApiError(message, response.status, fieldOf(payload));
  }
  if (payload !== null && typeof payload === 'object' && 'error' in payload) {
    throw new ApiError(String((payload as { error: unknown }).error), response.status);
  }
  return payload;
}

async function offline(path: string): Promise<unknown> {
  const response = await fetch(`${OFFLINE_BASE}/${path}`);
  if (!response.ok) throw new Error(`образец ${path} не найден (${response.status})`);
  return path.endsWith('.json') ? response.json() : response.text();
}

// --- определение живого сервера ---------------------------------------------

export interface ServerState {
  online: boolean;
  /** Почему сервера нет: показывается в шапке, чтобы не гадать. */
  reason: string | null;
  checkedAt: number;
}

/**
 * Проверка живого сервера.
 *
 * Мало получить код 200. Статический сервер (`vite preview`, любая
 * раздача `dist/`) отвечает на `/api/datasets` двумястами и HTML главной
 * страницы — и наивная проверка объявила бы такой сервер живым, после
 * чего приложение показало бы пустоту вместо образца. Поэтому проверка
 * требует разобранного JSON нужной формы, а не кода ответа.
 */
export async function probeServer(): Promise<ServerState> {
  try {
    await withTimeout(PROBE_TIMEOUT_MS, async (signal) => {
      const response = await fetch(API_BASE + '/datasets', { signal });
      const text = await response.text();
      let payload: unknown;
      try {
        payload = JSON.parse(text);
      } catch {
        // Тело читается ДО проверки кода ответа: сервер кладёт причину
        // отказа в {"error":"…"}, и «сервер ответил 400» вместо неё —
        // потеря ровно той подробности, ради которой её читают.
        if (!response.ok) throw new ApiError(`сервер ответил ${response.status}`, response.status);
        throw new ApiError('на /api/datasets ответил не JSON — это не сервер плейграунда', 200);
      }
      if (payload !== null && typeof payload === 'object' && 'error' in payload) {
        throw new ApiError(String((payload as { error: unknown }).error), response.status);
      }
      if (!response.ok) throw new ApiError(`сервер ответил ${response.status}`, response.status);
      const looksRight =
        Array.isArray(payload) ||
        (payload !== null &&
          typeof payload === 'object' &&
          ('datasets' in payload || 'error' in payload));
      if (!looksRight) {
        throw new ApiError('ответ /api/datasets не похож на список датасетов', 200);
      }
      return response;
    });
    return { online: true, reason: null, checkedAt: Date.now() };
  } catch (error) {
    const reason =
      error instanceof ApiError
        ? error.message
        : error instanceof DOMException && error.name === 'AbortError'
          ? `нет ответа за ${PROBE_TIMEOUT_MS} мс`
          : 'соединение не установилось';
    return { online: false, reason, checkedAt: Date.now() };
  }
}

// --- разбор JSONL -----------------------------------------------------------

export interface ParsedRuns {
  rows: RunRow[];
  /** Строки, которые не разобрались. Пропажу надо видеть, а не терять. */
  broken: number;
}

export function parseJsonl(text: string): ParsedRuns {
  const rows: RunRow[] = [];
  let broken = 0;
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed.length === 0) continue;
    try {
      const parsed = JSON.parse(trimmed) as RunRow;
      if (typeof parsed?.seed === 'number' && parsed.layout && parsed.population) {
        rows.push({ ...parsed, flags: parsed.flags ?? [] });
      } else {
        broken++;
      }
    } catch {
      broken++;
    }
  }
  return { rows, broken };
}

/** Ответ /runs и /simulate может приехать массивом, объектом или текстом JSONL. */
function coerceRuns(payload: unknown): RunRow[] {
  if (typeof payload === 'string') return parseJsonl(payload).rows;
  if (Array.isArray(payload)) return payload as RunRow[];
  if (payload !== null && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    for (const key of ['runs', 'rows', 'lines', 'data', 'result']) {
      const value = record[key];
      if (Array.isArray(value)) return value as RunRow[];
      if (typeof value === 'string') return parseJsonl(value).rows;
    }
  }
  return [];
}

// --- образец: индекс запечённых данных --------------------------------------

interface OfflineIndex {
  bakedAt: string;
  datasets: Array<{
    name: string;
    file: string;
    rows: number;
    configName: string | null;
    configHash: string | null;
    detailFile: string | null;
    detailRows: number;
  }>;
  presets: Preset[];
}

let offlineIndexCache: Promise<OfflineIndex> | null = null;
function offlineIndex(): Promise<OfflineIndex> {
  offlineIndexCache ??= offline('index.json') as Promise<OfflineIndex>;
  return offlineIndexCache;
}

// --- точки входа ------------------------------------------------------------

export interface Sourced<T> {
  value: T;
  origin: 'server' | 'sample';
  /** Что случилось с сервером, если ушли на образец. */
  fallbackReason: string | null;
}

async function tryServer<T>(
  online: boolean,
  fromServer: () => Promise<T>,
  fromSample: () => Promise<T>,
): Promise<Sourced<T>> {
  if (online) {
    try {
      return { value: await fromServer(), origin: 'server', fallbackReason: null };
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      return { value: await fromSample(), origin: 'sample', fallbackReason: reason };
    }
  }
  return { value: await fromSample(), origin: 'sample', fallbackReason: null };
}

export async function loadDatasets(online: boolean): Promise<Sourced<DatasetInfo[]>> {
  return tryServer<DatasetInfo[]>(
    online,
    async () => normalizeDatasets(await api('/datasets')),
    async () => {
      const index = await offlineIndex();
      return index.datasets.map((entry) => ({
        name: entry.name,
        configName: entry.configName,
        configHash: entry.configHash,
        rows: entry.rows,
        origin: 'sample' as const,
      }));
    },
  );
}

/**
 * Прогоны датасета целиком.
 *
 * Сервер отдаёт страницами и режет ответ по умолчанию на 500 строк, а
 * потолок limit — 5000. Взять первую страницу и считать её выборкой
 * значило бы молча посчитать перцентили по половине данных: числа
 * выглядели бы правдоподобно и были бы неверны. Поэтому страницы
 * дочитываются до конца, а не «сколько дали».
 */
export async function loadRuns(online: boolean, dataset: string): Promise<Sourced<RunRow[]>> {
  return tryServer<RunRow[]>(
    online,
    async () => {
      const rows: RunRow[] = [];
      let offset = 0;
      for (let guard = 0; guard < 200; guard++) {
        const payload = (await api(
          `/runs?dataset=${encodeURIComponent(dataset)}&limit=${RUNS_PAGE}&offset=${offset}`,
        )) as Record<string, unknown> | unknown[];
        const page = coerceRuns(payload);
        rows.push(...page);
        const total = Number((payload as Record<string, unknown>)?.filtered ?? NaN);
        offset += page.length;
        if (page.length === 0) break;
        if (Number.isFinite(total) ? rows.length >= total : page.length < RUNS_PAGE) break;
      }
      return rows;
    },
    async () => {
      const index = await offlineIndex();
      const entry = index.datasets.find((d) => d.name === dataset) ?? index.datasets[0];
      if (!entry) return [];
      const text = (await offline(entry.file)) as string;
      return parseJsonl(text).rows;
    },
  );
}

/**
 * Пресеты баланса. Отдельной точки API у них нет: это файлы
 * `data/presets/*.json`, и они запекаются в образец как есть. Стучаться
 * за ними на сервер незачем — там их не отдают, а 404 в консоли только
 * маскировал бы настоящие ошибки.
 */
/**
 * Пресеты: с живого сервера — GET /api/presets (за входом), без сервера —
 * из образца. Отказ НЕ бросается: без пресетов выключены только ползунки
 * «Обзора», а загрузка выборки обязана завершиться (раньше 403 на закрытом
 * offline/index.json рвал Promise.all в хранилище, и страница висела на
 * «загрузка выборки…»).
 */
export async function loadPresets(online: boolean): Promise<Sourced<Preset[]>> {
  try {
    if (online) {
      const payload = (await api('/presets')) as { presets?: unknown } | null;
      const list = Array.isArray(payload?.presets) ? (payload!.presets as Preset[]) : [];
      return { value: list, origin: 'server', fallbackReason: null };
    }
    const index = await offlineIndex();
    return { value: index.presets, origin: 'sample', fallbackReason: null };
  } catch (error) {
    return { value: [], origin: online ? 'server' : 'sample', fallbackReason: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * Телеметрия. Ответ сервера разбирается НАШИМ ЖЕ парсером сырых строк.
 *
 * Так офлайн и сервер дают побайтово одинаковые записи: снятие
 * дубликатов, обе схемы и группировка по сессиям живут в одном месте, а
 * не в двух похожих. Сервер при этом остаётся источником числа снятых
 * дубликатов — он видит оба файла-источника, а образец только один.
 */
export async function loadTelemetry(online: boolean): Promise<Sourced<TelemetryReport>> {
  return tryServer<TelemetryReport>(
    online,
    async () => {
      const payload = await api('/telemetry');
      const raw = telemetryRawLines(payload);
      if (raw === null) {
        throw new ApiError('в ответе /telemetry нет ни текста лога, ни сырых строк записей', 200);
      }
      const parsed = parseTelemetry(raw);
      const record = (payload ?? {}) as Record<string, unknown>;
      const serverDuplicates = Number(record.duplicatesRemoved);
      return {
        ...parsed,
        duplicatesRemoved: Number.isFinite(serverDuplicates)
          ? serverDuplicates
          : parsed.duplicatesRemoved,
        origin: 'server' as const,
      };
    },
    async () => {
      const text = (await offline('telemetry.log')) as string;
      return { ...parseTelemetry(text), origin: 'sample' as const };
    },
  );
}

export async function loadCatalog(online: boolean): Promise<Sourced<Catalog>> {
  return tryServer<Catalog>(
    online,
    async () => {
      // Каталог врагов и лут-таблицы приходят разными точками. Падение
      // /catalog/loot не должно уносить с собой каталог врагов: это
      // разные данные и разные причины отказа.
      const enemiesPayload = await api('/catalog/enemies');
      let lootPayload: unknown = null;
      try {
        lootPayload = await api('/catalog/loot');
      } catch {
        lootPayload = null;
      }
      const normalized = normalizeCatalog(enemiesPayload, lootPayload);
      if (normalized.lootTables.length === 0) {
        const baked = (await offline('catalog.json')) as Omit<Catalog, 'origin' | 'lootFromServer'>;
        normalized.lootTables = baked.lootTables;
        normalized.items = baked.items;
        normalized.warnings = baked.warnings ?? [];
        normalized.lootFromServer = false;
      }
      return { ...normalized, origin: 'server' as const };
    },
    async () => {
      const baked = (await offline('catalog.json')) as Partial<Catalog>;
      return {
        ...(baked as Omit<Catalog, 'origin' | 'lootFromServer'>),
        lootFromServer: false,
        warnings: baked.warnings ?? [],
        // Образец, испечённый до схемы 2, поля не содержит. null здесь
        // честнее пустой строки: страница по нему видит, что каталог
        // про дроп ничего не сказал, и говорит это словами.
        dropsNote: baked.dropsNote ?? null,
        origin: 'sample' as const,
      };
    },
  );
}

/**
 * Предметы и источники (GET /api/items). Все вероятности считает браузер,
 * поэтому без сервера вкладка работает так же — по модели, запечённой
 * тем же модулем сервера (scripts/bake-offline-fixtures.mjs). `refresh`
 * заставляет сервер перечитать ассеты: правка в инспекторе видна без
 * перезапуска.
 */
export async function loadItems(online: boolean, refresh = false): Promise<Sourced<ItemsPayload | null>> {
  const check = (payload: unknown): ItemsPayload => {
    const model = payload as ItemsPayload;
    if (!Array.isArray(model?.items) || !Array.isArray(model?.tables) || !Array.isArray(model?.budgets)) {
      throw new ApiError('ответ /items не похож на модель предметов', 200);
    }
    return model;
  };
  return tryServer<ItemsPayload | null>(
    online,
    async () => check(await api(refresh ? '/items?refresh=1' : '/items')),
    async () => {
      try {
        return check(await offline('items.json'));
      } catch {
        return null;
      }
    },
  );
}

/** Частоты модулей считаются только по живому пулу сцены, образца у них нет. */
export async function loadRoomSweep(online: boolean): Promise<Sourced<RoomSweepReport | null>> {
  if (!online) return { value: null, origin: 'sample', fallbackReason: null };
  try {
    const payload = await api('/rooms');
    const report = payload as RoomSweepReport;
    if (!Number.isInteger(report?.runs) || !Array.isArray(report?.modules)) {
      throw new ApiError('ответ /rooms не похож на отчёт по комнатам', 200);
    }
    return { value: report, origin: 'server', fallbackReason: null };
  } catch (error) {
    return {
      value: null,
      origin: 'sample',
      fallbackReason: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Статистика появления врагов и лута по комнатам.
 *
 * С сервером берётся его сведённый ответ /spawns: он считает то же самое
 * по тысяче seed и заодно проверяет склейку с файлом прогонов. Без
 * сервера то же самое считается в браузере из запечённого файла
 * подробностей — числа обязаны совпадать, поэтому формулы одни и те же.
 */
export async function loadSpawnStats(
  online: boolean,
  dataset: string,
): Promise<Sourced<SpawnStatistics | null>> {
  return tryServer<SpawnStatistics | null>(
    online,
    async () => {
      const stats = normalizeSpawns(await api(`/spawns?dataset=${encodeURIComponent(dataset)}`));
      if (stats === null) throw new ApiError('ответ /spawns не содержит разбивки по комнатам', 200);
      return stats;
    },
    async () => {
      const index = await offlineIndex();
      const entry = index.datasets.find((d) => d.name === dataset) ?? index.datasets[0];
      if (!entry?.detailFile) return null;
      const text = (await offline(entry.detailFile)) as string;
      const rows = coerceDetails(text);
      return rows.length > 0 ? computeSpawns(rows) : null;
    },
  );
}

function coerceDetails(payload: unknown): DetailRow[] {
  const rows: DetailRow[] = [];
  const push = (candidate: unknown) => {
    const row = candidate as DetailRow;
    if (typeof row?.seed === 'number' && Array.isArray(row?.rooms)) rows.push(row);
  };
  if (typeof payload === 'string') {
    for (const line of payload.split(/\r?\n/)) {
      const trimmed = line.trim();
      if (trimmed.length === 0) continue;
      try {
        push(JSON.parse(trimmed));
      } catch {
        /* строка не разобралась — пропускаем, число видно по итогу */
      }
    }
    return rows;
  }
  if (Array.isArray(payload)) {
    for (const entry of payload) push(entry);
    return rows;
  }
  if (payload !== null && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    for (const key of ['details', 'rows', 'runs', 'data', 'spawns']) {
      const value = record[key];
      if (value !== undefined) return coerceDetails(value);
    }
  }
  return rows;
}

/**
 * Четыре вида находок от сервера (`/stats` → `findings`).
 *
 * Без сервера возвращает null, и страница «Аномалии» считает свои —
 * по тем же четырём видам, но по строкам в браузере. Числа могут
 * разойтись в мелочах (у сервера богаче набор метрик), и интерфейс
 * показывает, чьи он показывает.
 */
export async function loadFindings(
  online: boolean,
  dataset: string,
): Promise<Sourced<ServerFindings | null>> {
  if (!online) return { value: null, origin: 'sample', fallbackReason: null };
  try {
    const payload = await api(`/stats?dataset=${encodeURIComponent(dataset)}`);
    return { value: normalizeFindings(payload), origin: 'server', fallbackReason: null };
  } catch (error) {
    return {
      value: null,
      origin: 'sample',
      fallbackReason: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Парное сравнение с сервера. Нужно ради двух вещей, которых в строках
 * нет: смены блока измерения у метрики и предупреждения о том, что число
 * выбросов двух выборок напрямую не сравнивается.
 */
export async function loadComparison(
  online: boolean,
  a: string,
  b: string,
): Promise<Sourced<ComparisonExtras | null>> {
  if (!online) return { value: null, origin: 'sample', fallbackReason: null };
  try {
    const payload = await api(
      `/compare?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`,
    );
    return { value: normalizeComparison(payload), origin: 'server', fallbackReason: null };
  } catch (error) {
    return {
      value: null,
      origin: 'sample',
      fallbackReason: error instanceof Error ? error.message : String(error),
    };
  }
}

// --- пересчёт под ползунки --------------------------------------------------

export interface SimulateRequest {
  config: BalanceConfig;
  runs: number;
  seedFrom: number;
}

export interface SimulateResult extends SimulateSummary {
  /** Сколько ждали ответа по часам браузера. */
  roundTripMs: number;
}

/**
 * POST /simulate. Единственный вызов, у которого НЕТ отката на образец:
 * пересчитать выборку без настоящей симуляции невозможно, а показать
 * вместо неё правдоподобную картинку — худшее, что может сделать
 * балансовый инструмент (ADR-0023, «почему не переписать симуляцию на JS»).
 *
 * Ответ — сводка, а не строки прогонов: перцентили, доли и аномалии
 * считает сервер, который эти строки и породил.
 */
export async function simulate(request: SimulateRequest): Promise<SimulateResult> {
  const started = performance.now();
  const payload = await api(
    '/simulate',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    },
    SIMULATE_TIMEOUT_MS,
  );
  const normalized = normalizeSimulate(payload);
  if (normalized.summary.rows === 0) {
    throw new ApiError('сервер вернул сводку без единого прогона', 200);
  }
  return { ...normalized, roundTripMs: performance.now() - started };
}

/**
 * Команда воспроизведения одного seed. Кладётся в буфер обмена.
 *
 * Запускается из корня репозитория игры (там CLI), а пресеты живут в
 * репозитории сервиса — где он лежит у человека, мы не знаем, поэтому путь
 * к нему остаётся заготовкой `<SimTool>`, а не догадкой про соседнюю папку.
 */
export function reproduceCommand(dataset: string, seed: number, configFile?: string | null): string {
  const config = configFile ? ` --config <SimTool>/data/presets/${configFile}` : '';
  return `dotnet run --project Tools/SimCli -c Release -- balance --runs 1 --seed-from ${seed}${config}` +
    `  # из корня игры; dataset: ${dataset}`;
}

// --- тюнинг ассетов (PLAN.md, B3) ---------------------------------------------
//
// Без отката на образец, как и /simulate: ползунки по настоящим ассетам без
// сервера не имеют смысла, а нарисованное по старому образцу выглядело бы
// как ответ на текущие числа.

export async function loadBudgets(): Promise<BudgetEntry[]> {
  return normalizeBudgets(await api('/budgets'));
}

export async function loadSchema(budget: string): Promise<TuningSchema> {
  return normalizeSchema(await api(`/schema?budget=${encodeURIComponent(budget)}`, undefined, SIMULATE_TIMEOUT_MS));
}

export interface RevisionInfo {
  known: boolean;
  gitRevision: string | null;
  dirty: boolean;
  branch: string | null;
  buildError: { gitRevision: string | null; at: string | null; logTail: string } | null;
}

export async function loadRevision(): Promise<RevisionInfo> {
  const root = ((await api('/revision')) ?? {}) as Record<string, unknown>;
  const error = root.buildError as Record<string, unknown> | null | undefined;
  return {
    known: root.known === true,
    gitRevision: typeof root.gitRevision === 'string' ? root.gitRevision : null,
    dirty: root.dirty === true,
    branch: typeof root.branch === 'string' ? root.branch : null,
    buildError:
      error && typeof error === 'object'
        ? {
            gitRevision: typeof error.gitRevision === 'string' ? error.gitRevision : null,
            at: typeof error.at === 'string' ? error.at : null,
            logTail: typeof error.logTail === 'string' ? error.logTail : '',
          }
        : null,
  };
}

export interface AssetsSimulateRequest {
  budget: string;
  runs: number;
  overrides: Overrides;
}

/**
 * POST /simulate по ассетам. Ошибка данных — ApiError с field.
 * Отмены нет: устаревший ответ отбрасывает страница по номеру запроса,
 * а сервер его всё равно досчитает и положит в кэш.
 */
export async function simulateAssets(request: AssetsSimulateRequest): Promise<TuningResult> {
  const payload = await api(
    '/simulate',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    },
    SIMULATE_TIMEOUT_MS,
  );
  return normalizeTuningResult(payload);
}
